import { createFileRoute, redirect, useRouterState } from '@tanstack/react-router'
import { getSharedStore } from '@migrate/stores'
import { AppAdapters } from '@shared/adapter'
import ErrorBoundary from '@migrate/components/ErrorBoundary'
import MercatusGlobalContextProvider from '@migrate/components/MercatusGlobalContextProvider'
import { Notifier, UserActivityMonitor } from '@shared/components'
import { CenteredSpinner } from '@portal/pkg-ui'
import { ScopedEnablementQueries } from '@portal/pkg-authorization'
import { UserInfoQueries } from '@shared/data'
import { deleteIdleLogoutEntry } from '@auth/data/idleLogout'
import { DEFAULT_REDIRECT_TARGET } from '@auth/redirectTarget'
import ResponsiveNav from '@migrate/components/ResponsiveNav'

/**
 * Before CRPM-4210, a tab logged in again after an idle timeout came back to `/?idle=1`. A tab that
 * went to the identity provider before the deploy can still arrive there, so the marker is stripped.
 * Remove this in the release after CRPM-4210.
 */
function isLegacyIdleLoginReturn(href: string): boolean {
  const url = new URL(href, window.location.origin)
  return (
    url.pathname === DEFAULT_REDIRECT_TARGET &&
    !url.hash &&
    [...url.searchParams].length === 1 &&
    url.searchParams.get('idle') === '1'
  )
}

export const Route = createFileRoute('/_auth')({
  component: RouteComponent,
  beforeLoad: async ({ context: { queryClient }, location }) => {
    let authContext
    try {
      const [effectiveUser, scopedEnablements] = await Promise.all([
        queryClient.ensureQueryData(UserInfoQueries.userInfo()),
        queryClient.ensureQueryData(ScopedEnablementQueries.scopedEnablements()),
      ])

      // Inject user and enablements into context.
      authContext = {
        effectiveUser: effectiveUser.impersonated ?? effectiveUser.real,
        realUser: effectiveUser.real,
        scopedEnablements,
      }
    } catch {
      // Logged out: log in, then go on to the page asked for. A link never goes to the Resume page,
      // even in a tab waiting there; that tab's saved place is dropped once it's logged in. Logouts
      // reach the Resume page directly, because the BFF's logout ends there.
      throw redirect({
        to: '/login',
        search: { to: location.href },
      })
    }

    // Logged in on an ordinary page, so any place this tab saved earlier is stale: the page asked
    // for wins. Restoring happens only through the Resume page.
    deleteIdleLogoutEntry()

    // Thrown outside the try above, which would otherwise catch the redirect and send a logged-in
    // user to the login page.
    if (isLegacyIdleLoginReturn(location.href)) {
      throw redirect({ to: DEFAULT_REDIRECT_TARGET, replace: true })
    }

    return authContext
  },
  loader: async ({ context: { queryClient } }) => {
    // Ensure enablements are loaded before continuing; this is loaded into context in the Router
    // and used for access checking in routes.
    const promises: Array<Promise<any>> = [
      queryClient.ensureQueryData(ScopedEnablementQueries.scopedEnablements()),
    ]
    if (!getSharedStore().ready) {
      promises.push(getSharedStore().load())
    }
    await Promise.all(promises)
  },
})

function RouteComponent() {
  const isLoading = useRouterState({ select: (s) => s.status === 'pending' })

  return (
    <AppAdapters>
      <ErrorBoundary>
        <MercatusGlobalContextProvider>
          <UserActivityMonitor>
            <ResponsiveNav />
            <Notifier />
            {isLoading && <CenteredSpinner />}
          </UserActivityMonitor>
        </MercatusGlobalContextProvider>
      </ErrorBoundary>
    </AppAdapters>
  )
}
